import React from 'react';
import { compose, withHandlers, withState } from 'recompose';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import ImageUpload from '../../../../../common/components/ImageUpload/ImageUpload';
import { uploadLandingPageLogo } from '@mm/state/actions/uploadActions';
import LandingPageCRUDActions from '@mm/state/actions/LandingPageCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import LandingPageModel from '@mm/state/model/LandingPageModel';
import './LogoEditor.scss';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';

type OuterProps = {
  formImageUrl: string;
  formImageNewUrl: string;
  selectedEntity: LandingPageModel;
  setFormImageNewUrl: Function;
  setFormImageUrl: Function;
};

type PropsFromState = ReturnType<typeof mapStateToProps>;
type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;
type PropsFromWithState = {
  showLoader: boolean;
  setShowLoader: (value: boolean) => boolean;
};
type PropsBeforeWithHandlers = OuterProps &
  PropsFromState &
  PropsFromDispatch &
  PropsFromWithState;

type PropsFromWithHandlers = {
  handleUpload: ReturnType<HandleUpload>;
  handleRemove: HandleRemove;
};

type Props = PropsBeforeWithHandlers & PropsFromWithHandlers;

type InnerProps = PropsFromState &
  PropsFromDispatch &
  PropsFromWithState &
  PropsFromWithHandlers;

const LogoEditorBase = (props: Props) => {
  const {
    formImageUrl,
    formImageNewUrl,
    handleUpload,
    handleRemove,
    showLoader,
    selectedEntity,
  } = props;

  let imgUrl = '';

  if (selectedEntity) {
    if (formImageNewUrl) {
      imgUrl = `${selectedEntity.landingBaseUrl}${formImageNewUrl}`;
    } else if (formImageUrl) {
      imgUrl = `${selectedEntity.landingBaseUrl}${formImageUrl}?${Date.now()}`;
    }
  }

  return (
    <div className="logo-editor">
      <div className="logo-editor__heading">Logo</div>
      <div className="logo-editor__image-container">
        <ImageUpload
          className="logo-editor__upload-container"
          title={'Upload picture'}
          imgUrl={imgUrl}
          uploadImage={handleUpload}
          removeImage={handleRemove}
          onRatioChange={() => {}}
          couldBeRemoved
          iconSize={3}
          showLoader={showLoader}
          hideCropIcon={true}
        />
      </div>
    </div>
  );
};

type HandleUpload = (
  props: PropsBeforeWithHandlers,
) => (img?: any) => Promise<any>;

const handleUpload: HandleUpload = props => async img => {
  const { setShowLoader, setFormImageNewUrl, uploadImage } = props;

  setShowLoader(true);
  const newLogoUrl = await uploadImage(img);

  // Adding Date.now to invalidate cache
  setFormImageNewUrl(`${newLogoUrl}?${Date.now()}`);
  setShowLoader(false);
};

type HandleRemove = (
  props: PropsBeforeWithHandlers,
) => (img: any) => Promise<void>;

const handleRemove: HandleRemove = props => async () => {
  const { formImageNewUrl, setFormImageNewUrl, setFormImageUrl, formImageUrl } =
    props;

  if (formImageNewUrl) {
    setFormImageNewUrl('');
  } else if (formImageUrl) {
    // Delete imageUrl
    setFormImageUrl('');
  }
};

const mapStateToProps = (state: StoreState) => ({
  botId: activeChatbotIdSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>, ownProps) => ({
  uploadImage: img64 =>
    dispatch(
      uploadLandingPageLogo(
        img64,
        ownProps.selectedEntity && ownProps.selectedEntity.id,
      ),
    ),
  getLandingPage: () =>
    dispatch(
      LandingPageCRUDActions.retrieveById(
        ownProps.botId,
        ownProps.selectedEntity && ownProps.selectedEntity.id,
      ),
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps),
  connect(null, mapDispatchToProps),
  withState('showLoader', 'setShowLoader', 'true'),
  withHandlers({
    handleUpload,
    handleRemove,
  }),
);

export default enhance(LogoEditorBase);
